import { useState, type FormEvent, type ReactElement } from 'react';
import {
  INVITABLE_ROLES,
  roleLabel,
  type InvitableRole,
  type MemberUpdate,
  type TeamMember,
} from '@kratispot/shared';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/lib/i18n';

export type MemberDraft = { name: string; email: string; role: InvitableRole };

export const BLANK_DRAFT: MemberDraft = {
  name: '',
  email: '',
  role: INVITABLE_ROLES[0] as InvitableRole,
};

export function draftOf(member: TeamMember): MemberDraft {
  return { name: member.name ?? '', email: member.email, role: member.role as InvitableRole };
}

// Only what the owner actually altered travels, so §4 refuses nothing idly.
export function changesOf(member: TeamMember, draft: MemberDraft): MemberUpdate {
  return {
    ...(draft.name !== (member.name ?? '') ? { name: draft.name } : {}),
    ...(draft.email !== member.email ? { email: draft.email } : {}),
    ...(draft.role !== member.role ? { role: draft.role } : {}),
  };
}

export function MemberDialog({
  title,
  submitLabel,
  initial,
  note,
  emailLocked = false,
  onSubmit,
  onClose,
  error,
  busy,
}: {
  title: string;
  submitLabel: string;
  initial: MemberDraft;
  note?: string;
  emailLocked?: boolean;
  onSubmit: (draft: MemberDraft) => void;
  onClose: () => void;
  error: string | null;
  busy: boolean;
}): ReactElement {
  const { t, language } = useTranslation();
  const [name, setName] = useState(initial.name);
  const [email, setEmail] = useState(initial.email);
  const [role, setRole] = useState<InvitableRole>(initial.role);

  function submit(event: FormEvent): void {
    event.preventDefault();
    onSubmit({ name: name.trim(), email, role });
  }

  return (
    <div className="fixed inset-0 z-70 flex items-center justify-center p-4 pt-safe pb-safe">
      <button
        type="button"
        aria-label={t('close')}
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <form
        role="dialog"
        aria-label={title}
        onSubmit={submit}
        className="relative w-full max-w-sm rounded-2xl bg-card p-5 shadow-xl"
      >
        <h2 className="mb-4 text-base font-bold">{title}</h2>

        <label
          className="mb-1 block text-xs font-semibold text-muted-foreground"
          htmlFor="member-name"
        >
          {t('name')}
        </label>
        <input
          id="member-name"
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
          className="mb-3 w-full rounded-lg border px-3 py-2 text-base"
        />

        <label
          className="mb-1 block text-xs font-semibold text-muted-foreground"
          htmlFor="member-email"
        >
          {t('email')}
        </label>
        <input
          id="member-email"
          type="email"
          required
          disabled={emailLocked}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className="mb-1 w-full rounded-lg border px-3 py-2 text-base disabled:bg-muted disabled:text-muted-foreground"
        />
        {note !== undefined && <p className="mb-3 text-xs text-muted-foreground">{note}</p>}

        <label
          className="mt-2 mb-1 block text-xs font-semibold text-muted-foreground"
          htmlFor="member-role"
        >
          {t('role')}
        </label>
        <select
          id="member-role"
          value={role}
          onChange={(event) => setRole(event.target.value as InvitableRole)}
          className="mb-1 w-full rounded-lg border px-3 py-2 text-base"
        >
          {INVITABLE_ROLES.map((option) => (
            <option key={option} value={option}>
              {roleLabel(option, language)}
            </option>
          ))}
        </select>

        {error !== null && (
          <p role="alert" className="mt-3 rounded-lg bg-red-100 px-3 py-2 text-sm text-red-800">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={busy}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </div>
  );
}
